// Concrete recipe — Set3 §2 (cement → concrete): fbm(low-contrast) + fbm(high-freq speckle).
// NO cracks layer. Immutable built-in root (never mutate — extend/fork instead).
//
// Set3 §2.2 — the user's use-case is a CONTINUOUS POURED SLAB: no crack network, no cell
// tessellation.  The old `cement` recipe's worley "cracks" layer tessellated the surface into
// cobblestone-like cells (dark groove at every cell boundary — the "broken into chunks" zoomed-in
// read) and kept a full-contrast groove at every zoom (the "tiling effect" zoomed out — a uniform
// sub-pixel grid of dark lines).  Deleting the cracks layer removes both artifacts; what remains —
// base + speckle, both fbm — has no lattice periodicity by construction (fbm is band-limited
// noise), so no tiling is possible.
//
// SCALE CONTRACT (see Recipe_Dirt for the rationale — separated scales, height magnitudes ordered
// the same way as the scales):
//   base    ~2.5 m   colour only, very low contrast (the "poured slab" mottling)
//   speckle ~1.5 cm  aggregate grit; colour + a HAIR of relief (~0.4 mm)
// Concrete's realism is subtlety: the plan notes procedural does this BETTER than tiled photos
// precisely because there is zero repeat. Keep every contrast low.
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";

export const CONCRETE_RECIPE_ID = "concrete";

export const concreteRecipe = new MaterialRecipe({
	id: CONCRETE_RECIPE_ID,
	name: "Concrete",
	roughness: 0.75,
	specStrength: 0, // matte mineral surface — no coherent highlight (see Recipe_Dirt)

	params: [
		new ParamSchema({key: "concrete_scale", label: "Base scale (m)", kind: "scalar", min: 0.2, max: 20, default: 2.5}),
		// Deliberately narrow ramp: real concrete varies by a few percent in value, not in hue.
		// Set3 §2.3 — concrete is grey, slightly coarser and a touch darker than cement paste;
		// the ramp is the cement values as a starting point, tuned visually after the rename.
		new ParamSchema({key: "concrete_baseRamp", label: "Base ramp", kind: "ramp", default: [0, 0.52, 0.52, 0.51, 0.5, 0.57, 0.57, 0.56, 1, 0.62, 0.62, 0.6]}),
		new ParamSchema({key: "speckle_scale", label: "Speckle scale (m)", kind: "scalar", min: 0.002, max: 0.2, default: 0.015}),
		// This ramp is a MULTIPLIER (the layer blends `multiply`), so it must centre on ~1.0 — not
		// on an albedo value.  Written as an albedo (~0.6) it double-darkens the base and concrete
		// renders as dark grey.  Range 0.88..1.06 = a ±10% value jitter, which is the right
		// magnitude for exposed aggregate grit.
		new ParamSchema({key: "speckle_ramp", label: "Speckle ramp (multiplier)", kind: "ramp", default: [0, 0.88, 0.88, 0.87, 0.6, 0.97, 0.97, 0.96, 1, 1.06, 1.06, 1.04]}),
		// The aggregate grit's relief. Sub-millimetre: concrete is a smooth troweled surface;
		// Set3 §2.3 bumps it 0.0002 → 0.0004 so the aggregate reads as fine texture under the
		// specular lobe, still sub-millimetre (anything larger reads as gravel).
		new ParamSchema({key: "speckle_height", label: "Speckle relief (m)", kind: "scalar", min: 0, max: 0.01, default: 0.0004}),
	],
	layers: [
		// Base: colour only, no height (a metres-scale fbm's relief would dominate everything above it).
		new MaterialLayer({
			id: "base",
			primitive: "fbm",
			args: {
				scale: {ref: "concrete_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.55,
				mode: 0, // plain fbm — concrete's macro variation is smooth/cloudy, NOT clumped (unlike dirt's turbulence)
				colorRamp: {ref: "concrete_baseRamp"},
				heightScale: 0,
			},
			blend: "over",
			contributes: "color",
		}),
		// Aggregate speckle: multiplied in so it MODULATES the base rather than replacing it (an
		// `over` here would discard the base mottling entirely — the base layer would be dead code).
		// NOTE its ramp is a MULTIPLIER centred on 1.0, not an albedo (see the param's comment).
		new MaterialLayer({
			id: "speckle",
			primitive: "fbm",
			args: {
				scale: {ref: "speckle_scale"},
				octaves: 2,
				lacunarity: 2.3,
				persistence: 0.5,
				mode: 3, // turbulence — abs-sum gives the pitted/granular read of exposed aggregate
				colorRamp: {ref: "speckle_ramp"},
				heightScale: {ref: "speckle_height"},
			},
			blend: "multiply",
			contributes: "both",
		}),
	],
});
